// Authentication Service for HttpOnly Cookie & Session Management
import axios from "axios";
import { apiClient } from "../api/apiClient.js";
import { assertSafePayload } from "../utils/inputValidation.js";
import authApi from "../api/auth.api.js";
import { RoleId } from "../types/auth.js";

const API_BASE_URL =
  process.env.REACT_APP_M2 || process.env.REACT_APP_API_URL || "/api";

// Storage keys
const TOKEN_KEY = "token";
const USER_KEY = "user_data";
const AUTH_CHANNEL_NAME = "hospital_auth_channel";

// Authentication Service Class
class AuthService {
  constructor() {
    this._user = null;
    this._sessionChecked = false;
    this._sessionPromise = null;
    this._authChannel = null;

    this.can = this.can.bind(this);
    this.canAny = this.canAny.bind(this);
    this.canAll = this.canAll.bind(this);
    this.hasRole = this.hasRole.bind(this);

    this.initBroadcastChannel();
    this.initUnauthorizedListener();

    // Purge any legacy token and user data from localStorage
    this.purgeLegacyStorage();
  }

  /**
   * Purge legacy tokens and user metadata from browser storage to prevent XSS exposure
   */
  purgeLegacyStorage() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem("user_info");
      localStorage.removeItem("selectedPatientData");
      sessionStorage.removeItem("selectedPatientData");
    } catch (e) {}
  }

  /**
   * Initializes BroadcastChannel for multi-tab logout and session synchronization.
   */
  initBroadcastChannel() {
    if (
      typeof window !== "undefined" &&
      typeof BroadcastChannel !== "undefined"
    ) {
      try {
        this._authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        this._authChannel.onmessage = (event) => {
          if (event.data?.type === "LOGOUT") {
            this.handleExternalLogout(event.data?.reason);
          }
        };
      } catch (e) {
        console.warn("BroadcastChannel initialization skipped:", e.message);
      }
    }
  }

  /**
   * Listens for 401 events emitted by Axios or Fetch interceptors.
   */
  initUnauthorizedListener() {
    if (typeof window !== "undefined") {
      window.addEventListener("auth:unauthorized", (event) => {
        // Only act on unauthorized events if the user was actually
        // authenticated; otherwise we'd trigger a redundant logout cycle
        // on the login page.
        if (this.isAuthenticated()) {
          this.notifySessionTerminated(
            event.detail?.message || "Session expired.",
          );
        }
      });
    }
  }

  /**
   * Handles logout initiated from another browser tab.
   */
  handleExternalLogout(reason = "session_terminated") {
    this.removeToken();
    this.removeUser();
    this.stopInactivityTimer();
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}

    const redirectUrl =
      reason === "session_terminated"
        ? "/login?reason=session_terminated"
        : "/login";

    if (typeof window !== "undefined") {
      const currentUrl = window.location.pathname + window.location.search;
      if (window.location.pathname !== "/login") {
        window.location.replace(redirectUrl);
      } else if (currentUrl !== redirectUrl) {
        window.history.replaceState(null, null, redirectUrl);
      }
    }
  }

  /**
   * Workstation inactivity timer (disabled)
   */
  startInactivityTimer() {}

  /**
   * Stops the workstation inactivity timer (disabled)
   */
  stopInactivityTimer() {}

  /**
   * Session Bootstrapping Pattern (/api/me)
   * Verifies active HttpOnly cookie session on initial page reload.
   * If already verified in memory, returns immediately without triggering redundant HTTP requests.
   */
  async checkSession(force = false) {
    if (!force && this._user) {
      return { authenticated: true, user: this._user };
    }

    if (!force && this._sessionChecked && !this._user) {
      return { authenticated: false, user: null };
    }

    if (this._sessionPromise) {
      return this._sessionPromise;
    }

    this._sessionPromise = (async () => {
      try {
        const rawUser = await authApi.getProfile();
        if (rawUser) {
          const user = { ...(rawUser.data || rawUser.user || rawUser) };
          delete user.access_token;
          delete user.token;
          this.setUser(user);
          this._sessionChecked = true;
          this.startInactivityTimer();
          return { authenticated: true, user };
        }
      } catch (error) {
        if (error.response?.status === 401) {
          // Attempt silent refresh before marking user as unauthenticated
          const refreshed = await this.refreshToken();
          if (refreshed) {
            try {
              const retryUser = await authApi.getProfile();
              if (retryUser) {
                const user = { ...(retryUser.data || retryUser.user || retryUser) };
                delete user.access_token;
                delete user.token;
                this.setUser(user);
                this._sessionChecked = true;
                this.startInactivityTimer();
                return { authenticated: true, user };
              }
            } catch (retryError) {
              console.warn("Retry getProfile after refresh failed:", retryError?.message);
            }
          }
          this.removeUser();
          this._sessionChecked = true;
          this.stopInactivityTimer();
          return { authenticated: false, user: null };
        }
        console.warn("Session verification issue:", error?.message);
      } finally {
        this._sessionPromise = null;
      }
      return {
        authenticated: this.isAuthenticated(),
        user: this.getUser(),
      };
    })();

    return this._sessionPromise;
  }

  /**
   * Login user with email and password (HttpOnly cookie enabled)
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login response with user data
   */
  async login(email, password) {
    try {
      const credentials = { email, password };
      assertSafePayload(credentials, "login");
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        credentials: "include", // Enables HttpOnly cookie receipt
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMsg = data.data || data.msg || data.message || "Login failed";
        const error = new Error(errorMsg);
        error.data = data.data;
        error.msg = data.msg;
        error.code = data.code || response.status;
        throw error;
      }

      if (data.code === 200 && data.data) {
        // Store user details for UI presentation (sanitize out any tokens)
        let userData = { ...data.data };
        delete userData.access_token;
        delete userData.token;
        this.setUser(userData);

        // Fetch complete user profile from /api/me immediately so full real name and role are populated
        try {
          const session = await this.checkSession(true);
          if (session?.user) {
            userData = session.user;
          }
        } catch (profileErr) {
          console.warn("Could not fetch full user profile immediately after login:", profileErr);
        }

        // Ensure no leftover token is in localStorage
        this.removeToken();

        // Broadcast login
        if (this._authChannel) {
          this._authChannel.postMessage({ type: "LOGIN", user: userData });
        }

        // Start 15-minute inactivity timer
        this.startInactivityTimer();

        return {
          success: true,
          data: userData,
          message: "Login successful",
        };
      } else {
        throw new Error(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  /**
   * Logout user and notify backend to clear HttpOnly cookies & revoke token
   */
  async logout(reason = "user_action") {
    try {
      // Call backend logout to invalidate session & clear HttpOnly cookies
      await fetch(`${API_BASE_URL}/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeader(),
        },
      }).catch(() => {});
    } catch (e) {}

    this.removeToken();
    this.removeUser();
    this.stopInactivityTimer();

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}

    // Synchronize logout across all open browser tabs
    if (this._authChannel) {
      this._authChannel.postMessage({ type: "LOGOUT", reason });
    }

    const redirectUrl =
      reason === "session_terminated"
        ? "/login?reason=session_terminated"
        : "/login";

    if (typeof window !== "undefined") {
      const currentUrl = window.location.pathname + window.location.search;
      if (window.location.pathname !== "/login") {
        window.history.replaceState(null, null, redirectUrl);
        window.location.replace(redirectUrl);
      } else if (currentUrl !== redirectUrl) {
        window.history.replaceState(null, null, redirectUrl);
      }
    }
  }

  /**
   * Enhanced logout with navigation hook support
   * @param {Function} navigate - React Router navigate function
   * @param {string} reason - Logout reason
   */
  async logoutWithNavigate(navigate, reason = "user_action") {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeader(),
        },
      }).catch(() => {});
    } catch (e) {}

    this.removeToken();
    this.removeUser();
    this.stopInactivityTimer();

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}

    if (this._authChannel) {
      this._authChannel.postMessage({ type: "LOGOUT", reason });
    }

    const redirectUrl =
      reason === "session_terminated"
        ? "/login?reason=session_terminated"
        : "/login";

    if (navigate) {
      navigate(redirectUrl, { replace: true });
    } else if (typeof window !== "undefined") {
      const currentUrl = window.location.pathname + window.location.search;
      if (window.location.pathname !== "/login") {
        window.location.replace(redirectUrl);
      } else if (currentUrl !== redirectUrl) {
        window.history.replaceState(null, null, redirectUrl);
      }
    }
  }

  /**
   * Notify session termination from 401 response interceptor
   */
  notifySessionTerminated(message = "Session expired.") {
    this.logout("session_terminated");
  }

  /**
   * Deprecated / No-op: Authentication is managed exclusively via secure HttpOnly cookies.
   */
  setToken(token) {
    // Purposely no-op: tokens are never stored in localStorage to prevent XSS exposure
  }

  /**
   * Get JWT token (tokens are stored in HttpOnly cookies and not readable via JS)
   */
  getToken() {
    return null;
  }

  /**
   * Remove JWT token from localStorage (purges any legacy keys)
   */
  removeToken() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {}
  }

  /**
   * Store user data in memory only (never stored in localStorage)
   */
  setUser(user) {
    this._user = user ? { ...user } : null;
    this._sessionChecked = !!user;
    this.purgeLegacyStorage();
    if (user) {
      this.setupTokenRefresh();
    } else if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("auth:user_changed", { detail: { user: this._user } }),
      );
    }
  }

  /**
   * Get user data from in-memory store
   */
  getUser() {
    return this._user;
  }

  /**
   * Remove user data from in-memory store and purge legacy storage
   */
  removeUser() {
    this._user = null;
    this._sessionChecked = false;
    this._sessionPromise = null;
    this.purgeLegacyStorage();
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("auth:user_changed", { detail: { user: null } }),
      );
    }
  }

  /**
   * Check if user is authenticated (validates in-memory user presence)
   */
  isAuthenticated() {
    return !!this._user;
  }

  /**
   * Check if initial session verification has been completed
   */
  isSessionChecked() {
    return this._sessionChecked;
  }

  /**
   * Check if current user has a specific permission.
   * Directly validates against unified permissions array from /me.
   * Internal helper to match permissions with bidirectional aliases
   * (handles naming differences between backend and frontend constants)
   */
  _matchesPermission(userPerms, perm) {
    if (!Array.isArray(userPerms) || !perm) return false;
    if (userPerms.includes(perm)) return true;

    const ALIAS_MAP = {
      "billing:read_invoice": "billing:read",
      "billing:read": "billing:read_invoice",
      "billing:create_invoice": "billing:create",
      "billing:create": "billing:create_invoice",
      "billing:collect_payment": "billing:process_payment",
      "billing:process_payment": "billing:collect_payment",
      "lab:read_results": "lab:read",
      "lab:read": "lab:read_results",
      "audit_logs:read": "system:audit_logs",
      "system:audit_logs": "audit_logs:read",
    };

    const alias = ALIAS_MAP[perm];
    return Boolean(alias && userPerms.includes(alias));
  }

  /**
   * Check if current user has a specific permission.
   * (No blanket super admin bypass: preserves clinical governance separation)
   * @param {string} permission
   * @returns {boolean}
   */
  can(permission) {
    if (!this._user || !this._user.permissions) return false;
    return this._matchesPermission(this._user.permissions, permission);
  }

  /**
   * Check if current user has ANY of the specified permissions.
   * @param {...string|string[]} permissions
   * @returns {boolean}
   */
  canAny(...permissions) {
    if (!this._user || !this._user.permissions) return false;
    const perms = Array.isArray(permissions[0]) ? permissions[0] : permissions;
    return perms.some((p) => this._matchesPermission(this._user.permissions, p));
  }

  /**
   * Check if current user has ALL of the specified permissions.
   * @param {...string|string[]} permissions
   * @returns {boolean}
   */
  canAll(...permissions) {
    if (!this._user || !this._user.permissions) return false;
    const perms = Array.isArray(permissions[0]) ? permissions[0] : permissions;
    return perms.every((p) => this._matchesPermission(this._user.permissions, p));
  }

  /**
   * Check if current user has any of the specified roles.
   * @param {...number|number[]} roleIds
   * @returns {boolean}
   */
  hasRole(...roleIds) {
    if (!this._user) return false;
    const roles = Array.isArray(roleIds[0]) ? roleIds[0] : roleIds;
    return roles.map(Number).includes(Number(this._user.role_id));
  }

  /**
   * Check if token is expired (handled by server on HttpOnly cookie)
   */
  isTokenExpired(token) {
    return false;
  }

  /**
   * Get authorization header (HttpOnly cookies are sent automatically via credentials: "include")
   */
  getAuthHeader() {
    return {};
  }

  /**
   * Refresh session via HttpOnly cookie endpoint
   */
  async refreshToken() {
    try {
      await authApi.refreshToken();
      return true;
    } catch (e) {
      console.warn("Silent refresh failed:", e.message);
      return false;
    }
  }

  /**
   * Get active sessions for current user (Max 3 allowed concurrent)
   */
  async getActiveSessions() {
    return authApi.getActiveSessions();
  }

  /**
   * Revoke a specific active session
   */
  async revokeSession(sessionId) {
    return authApi.revokeSession(sessionId);
  }

  /**
   * Revoke all other active sessions except current
   */
  async revokeAllOtherSessions() {
    return authApi.revokeAllOtherSessions();
  }

  /**
   * Setup periodic session refresh
   */
  setupTokenRefresh() {
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
    }
    // Periodically refresh session every 10 minutes while active
    this._refreshInterval = setInterval(() => {
      if (this.isAuthenticated()) {
        this.refreshToken();
      }
    }, 10 * 60 * 1000);
  }

  /**
   * Request password reset link
   */
  async forgotPassword(email) {
    try {
      assertSafePayload({ email }, "forgot-password");
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        let errorMsg =
          data.msg || data.message || "Failed to send reset link.";
        if (
          errorMsg ===
          "Too many password reset requests. Please try again later."
        ) {
          errorMsg = "Too many attempts. Please try again in 1 hour.";
        }
        const err = new Error(errorMsg);
        err.code = data.code || response.status;
        err.errors = data.errors;
        throw err;
      }

      return data;
    } catch (error) {
      console.error("Forgot password error:", error);
      throw error;
    }
  }

  /**
   * Verify password reset token validity and TTL
   */
  async verifyResetToken(token) {
    try {
      assertSafePayload({ token }, "verify-reset-token");
      const response = await fetch(`${API_BASE_URL}/verify-reset-token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMsg =
          data.msg ||
          data.message ||
          data.errors?.token ||
          "Invalid or expired password reset token or OTP";
        const err = new Error(errorMsg);
        err.code = data.code || response.status;
        err.errors = data.errors;
        throw err;
      }

      return data;
    } catch (error) {
      console.error("Verify reset token error:", error);
      throw error;
    }
  }

  /**
   * Submit new password with reset token
   */
  async resetPassword(token, password, confirmPassword) {
    try {
      assertSafePayload(
        { token, password, confirmPassword },
        "reset-password",
      );
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password, confirmPassword }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        let errorMsg =
          data.errors?.confirmPassword ||
          data.errors?.password ||
          data.errors?.token ||
          data.msg ||
          data.message ||
          data.data ||
          "Failed to reset password.";

        const err = new Error(errorMsg);
        err.code = data.code || response.status;
        err.errors = data.errors;
        err.msg = data.msg;
        err.data = data.data;
        throw err;
      }

      return data;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(currentPassword, newPassword, confirmNewPassword) {
    try {
      assertSafePayload(
        { currentPassword, newPassword, confirmNewPassword },
        "change-password",
      );

      const response = await apiClient.post("/change-password", {
        currentPassword,
        newPassword,
        confirmNewPassword,
      });

      return response.data;
    } catch (error) {
      if (error.response?.data) {
        const data = error.response.data;
        let errorMsg =
          data.errors?.confirmNewPassword ||
          data.errors?.newPassword ||
          data.errors?.currentPassword ||
          data.msg ||
          data.message ||
          data.data ||
          "Failed to change password.";

        if (errorMsg === "Invalid current password") {
          errorMsg = "Incorrect current password";
        }

        const err = new Error(errorMsg);
        err.code = data.code || error.response.status;
        err.errors = data.errors;
        err.msg = data.msg;
        err.data = data.data;
        throw err;
      }
      console.error("Change password error:", error);
      throw error;
    }
  }

  /**
   * Request OTP for password reset
   */
  async requestPasswordOtp(email) {
    try {
      assertSafePayload({ email }, "forgot-password-otp");
      const response = await axios.post(
        `${API_BASE_URL}/forgot-password-otp`,
        { email },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("Request password OTP error:", error);
      throw error;
    }
  }

  /**
   * Verify 6-digit OTP
   */
  async verifyPasswordOtp(email, otp) {
    try {
      assertSafePayload({ email, otp }, "verify-otp");
      const response = await axios.post(
        `${API_BASE_URL}/verify-otp`,
        { email, otp },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("Verify OTP error:", error);
      throw error;
    }
  }

  /**
   * Reset password using verified OTP
   */
  async resetPasswordWithOtp(params) {
    try {
      let payload = params;
      if (arguments.length > 1 || !params?.email) {
        payload = {
          email: arguments[0],
          otp: arguments[1],
          password: arguments[2],
          confirmPassword: arguments[3],
        };
      }
      assertSafePayload(payload, "reset-password-otp");
      const response = await axios.post(
        `${API_BASE_URL}/reset-password-otp`,
        payload,
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("Reset password with OTP error:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;

// Named exports for convenience
export const {
  login,
  logout,
  logoutWithNavigate,
  checkSession,
  setToken,
  getToken,
  removeToken,
  setUser,
  getUser,
  removeUser,
  isAuthenticated,
  isSessionChecked,
  isTokenExpired,
  getAuthHeader,
  refreshToken,
  setupTokenRefresh,
  startInactivityTimer,
  stopInactivityTimer,
  notifySessionTerminated,
  forgotPassword,
  verifyResetToken,
  resetPassword,
  changePassword,
  requestPasswordOtp,
  verifyPasswordOtp,
  resetPasswordWithOtp,
  getActiveSessions,
  revokeSession,
  revokeAllOtherSessions,
  can,
  canAny,
  canAll,
  hasRole,
} = authService;